import { useEffect, useRef } from 'react'
import { useBreakpoint } from '~/hooks'
import { useSpotSwapPair } from '~/state/dex/hooks'
import { BarData, createChart } from 'lightweight-charts'
import { dateToString, formatPrice } from './helper'
import { Container, StyledTooltip, StyledMobileTooltip } from './styled'
import { formatAmount } from '~/components/Exchange/helpers'
import { useTranslation } from 'react-i18next'

export const SwapCandleChart = ({ data, dateFormatStr }) => {
  const { i18n } = useTranslation()
  const locale = i18n.resolvedLanguage === 'zh' ? 'zh-cn' : 'en-us'

  const convertData = data.map((item) => ({
    ...item,
    // 毫秒 -> 秒
    time: item.timestamp / 1000,
  }))

  const { isMobile } = useBreakpoint()
  const [, outputToken] = useSpotSwapPair()
  const outputTokenPrecision = Math.min(8, outputToken?.decimals ?? 8)

  const chartContainerRef = useRef(null)
  const tooltipRef = useRef(null)
  const mobileTooltipRef = useRef(null)

  const tooltipWidth = 130
  const tooltipHeight = 61
  const tooltipMargin = 8

  const chartOptions = {
    layout: {
      fontSize: 11,
      textColor: '#6F7390',
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
    },
    leftPriceScale: {
      visible: true,
      borderColor: '#EFF0FA',
      ticksVisible: false,
    },
    rightPriceScale: {
      visible: false,
    },
    timeScale: {
      borderColor: '#EFF0FA',
      timeVisible: true,
      drawTicks: false,
      tickMarkFormatter: (time) => {
        return dateToString(time, dateFormatStr)
      },
    },
    grid: {
      vertLines: {
        visible: false,
      },
      horzLines: {
        visible: false,
      },
    },
    localization: {
      priceFormatter: (price) => {
        return formatPrice(price, outputTokenPrecision)
      },
      timeFormatter: (time) => {
        return dateToString(time)
      },
    },
    crosshair: {
      vertLine: {
        color: '#B2BFEB',
        style: 2,
        labelVisible: false,
      },
      horzLine: {
        color: '#B2BFEB',
        style: 2,
        labelVisible: false,
      },
    },
    handleScale: false,
    handleScroll: false,
  }

  useEffect(() => {
    const containerWidth = chartContainerRef.current.clientWidth
    const containerHeight = 350

    const handleResize = () => {
      chart.applyOptions({ width: chartContainerRef.current.clientWidth })
    }

    const chart = createChart(chartContainerRef.current, {
      width: containerWidth,
      height: containerHeight,
      ...chartOptions,
    })

    chart.timeScale().fitContent()

    const minMove = Math.pow(10, -outputTokenPrecision)

    const candleSeries = chart.addCandlestickSeries({
      priceScaleId: 'left',
      upColor: '#4dbc9b',
      downColor: '#df5b6b',
      borderVisible: false,
      wickUpColor: '#4dbc9b',
      wickDownColor: '#df5b6b',
      priceFormat: {
        type: 'price',
        precision: outputTokenPrecision,
        minMove,
      },
      priceLineStyle: 2,
    })
    candleSeries.priceScale().applyOptions({
      scaleMargins: {
        top: 0.1,
        bottom: 0.1,
      },
    })
    candleSeries.setData(convertData)

    const handlePCTooltip = (param) => {
      if (
        param.point === undefined ||
        !param.time ||
        param.point.x < 0 ||
        param.point.x > chartContainerRef.current.clientWidth ||
        param.point.y < 0 ||
        param.point.y > chartContainerRef.current.clientHeight
      ) {
        tooltipRef.current.style.display = 'none'
      } else {
        const dateStr = dateToString(param.time)
        tooltipRef.current.style.display = 'block'
        const { open, close } = param.seriesData.get(candleSeries) as BarData
        const formatOpen = formatAmount(open, outputTokenPrecision)
        const formatClose = formatAmount(close, outputTokenPrecision)
        const openLiteral = `Open: ${formatOpen}`
        const closeLiteral = `Close: ${formatClose}`
        tooltipRef.current.innerHTML = `<div style="color: #9598ab">${dateStr}</div><div>${openLiteral}</div><div>${closeLiteral}</div>`

        const y = param.point.y
        let left = param.point.x + tooltipMargin
        if (left > chartContainerRef.current.clientWidth - tooltipWidth) {
          left = param.point.x - tooltipMargin - tooltipWidth
        }

        let top = y + tooltipMargin
        if (top > chartContainerRef.current.clientHeight - tooltipHeight) {
          top = y - tooltipHeight - tooltipMargin
        }
        tooltipRef.current.style.left = left + 'px'
        tooltipRef.current.style.top = top + 'px'
      }
    }

    const handleMobileTooltip = (param) => {
      if (
        param.point === undefined ||
        !param.time ||
        param.point.x < 0 ||
        param.point.x > containerWidth ||
        param.point.y < 0 ||
        param.point.y > containerHeight
      ) {
        mobileTooltipRef.current.style.display = 'none'
      } else {
        const dateStr = dateToString(param.time)
        mobileTooltipRef.current.style.display = 'block'
        const { open, close } = param.seriesData.get(candleSeries) as BarData
        const formatOpen = formatAmount(open, outputTokenPrecision)
        const formatClose = formatAmount(close, outputTokenPrecision)
        const priceLiteral = `Open: ${formatOpen} | Close: ${formatClose}`
        mobileTooltipRef.current.innerHTML = `<div style="color: #9598ab">${dateStr}</div><div>${priceLiteral}</div>`
      }
    }

    // tooltip
    chart.subscribeClick(isMobile ? handleMobileTooltip : handlePCTooltip)
    chart.subscribeCrosshairMove(
      isMobile ? handleMobileTooltip : handlePCTooltip,
    )

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chart.remove()
    }
  }, [data, chartContainerRef.current?.clientWidth, isMobile, locale])

  return (
    <Container ref={chartContainerRef}>
      {isMobile ? (
        <StyledMobileTooltip ref={mobileTooltipRef} />
      ) : (
        <StyledTooltip ref={tooltipRef} />
      )}
    </Container>
  )
}
